/**
 * Hooks over the Phase 6-1 versioning endpoints.
 *
 * - `useAgentDetail(agentName)` → header info + stage counts.
 * - `useAgentVersions(agentName)` → full version history.
 * - `useAgentVersion(versionId)` → single row with full config.
 * - `useAuditLog(agentName?)` → recent audit log entries.
 *
 * All are thin fetch-once-on-mount with nonce refetch; no react-query
 * because that dep isn't installed in this repo. Each hook parses with
 * a shared Zod schema so the form components can trust the shape.
 */

import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { apiFetch } from "@/lib/jwt-auth-client";

const UserRefSchema = z
  .object({
    id: z.string(),
    email: z.string(),
    name: z.string().optional().default(""),
  })
  .nullable();

export const AgentVersionSchema = z.object({
  id: z.string(),
  agent_name: z.string(),
  unit: z
    .object({ id: z.string(), name: z.string() })
    .nullable(),
  version: z.number(),
  stage: z.enum(["draft", "staging", "active", "archived"]),
  system_prompt: z.string(),
  model_id: z.string(),
  // Empty string for agents that don't run image-gen tools; only meaningful
  // when at least one `brand_*_image_gen` tool is in `tool_names`. Defaults
  // tolerate older backend builds that don't emit the field.
  image_model_id: z.string().default(""),
  model_config: z.record(z.string(), z.unknown()).default({}),
  tool_names: z.array(z.string()),
  skill_names: z.array(z.string()),
  // Phase 3 — declared artifact subjects the agent consumes / produces.
  // Defaults make the schema tolerant of older Django builds that
  // pre-date the Phase 3.1 migration.
  inputs: z.array(z.string()).default([]),
  outputs: z.array(z.string()).default([]),
  max_steps: z.number(),
  sandbox_profile: z.string(),
  notes: z.string(),
  created_by: UserRefSchema,
  created_at: z.string().nullable(),
  submitted_for_review_by: UserRefSchema,
  submitted_for_review_at: z.string().nullable(),
  activated_by: UserRefSchema,
  activated_at: z.string().nullable(),
  // Phase 8 — dual-source authoring + per-agent runtime migration.
  // Defaults make the schema tolerant of older Django builds that don't
  // emit these fields yet.
  source: z.enum(["db", "anthropic_console"]).default("db"),
  external_agent_id: z.string().default(""),
  external_version_id: z.string().default(""),
  synced_at: z.string().nullable().default(null),
  runtime_target: z.enum(["unified", "anthropic_sdk"]).default("unified"),
  // Phase 17 — voice agent config.
  voice_enabled: z.boolean().default(false),
  voice_profile: z.record(z.string(), z.unknown()).default({}),
});

export type AgentVersion = z.infer<typeof AgentVersionSchema>;

const VersionsResponseSchema = z.object({
  versions: z.array(AgentVersionSchema),
  total: z.number(),
});

const VersionDetailSchema = z.object({ version: AgentVersionSchema });

const AgentDetailHeaderSchema = z.object({
  agent_name: z.string(),
  unit: z
    .object({ name: z.string(), display_name: z.string() })
    .nullable(),
  active_version_id: z.string().nullable(),
  latest_version_id: z.string(),
  stage_counts: z.record(z.string(), z.number()),
});

export type AgentDetailHeader = z.infer<typeof AgentDetailHeaderSchema>;

const AuditEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  actor: UserRefSchema,
  unit_name: z.string(),
  agent_name: z.string(),
  from_version: z.number().nullable(),
  to_version: z.number().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  created_at: z.string().nullable(),
});

const AuditLogResponseSchema = z.object({
  entries: z.array(AuditEntrySchema),
  total_returned: z.number(),
});

export type AuditEntry = z.infer<typeof AuditEntrySchema>;

interface Fetcher<T> {
  data: T | undefined;
  loading: boolean;
  error: string | undefined;
  refetch: () => void;
}

function useJsonFetch<T>(
  url: string | undefined,
  parse: (raw: unknown) => T,
): Fetcher<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(!!url);
  const [error, setError] = useState<string | undefined>(undefined);
  const [nonce, setNonce] = useState(0);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!url) {
      setData(undefined);
      setLoading(false);
      setError(undefined);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiFetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((raw) => {
        if (cancelled) return;
        setData(parse(raw));
        setError(undefined);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // parse is stable per caller; URL + nonce are the drivers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, nonce]);

  return { data, loading, error, refetch };
}

export function useAgentDetail(agentName: string | undefined) {
  return useJsonFetch(
    agentName
      ? `/agent/agent-detail/?agent_name=${encodeURIComponent(agentName)}`
      : undefined,
    (raw) => AgentDetailHeaderSchema.parse(raw),
  );
}

export function useAgentVersions(agentName: string | undefined) {
  return useJsonFetch(
    agentName
      ? `/agent/versions/?agent_name=${encodeURIComponent(agentName)}`
      : undefined,
    (raw) => VersionsResponseSchema.parse(raw),
  );
}

export function useAgentVersion(versionId: string | number | undefined) {
  return useJsonFetch(
    versionId ? `/agent/versions/${versionId}/` : undefined,
    (raw) => VersionDetailSchema.parse(raw).version,
  );
}

export function useAuditLog(agentName: string | undefined) {
  return useJsonFetch(
    agentName
      ? `/agent/audit-log/?agent_name=${encodeURIComponent(agentName)}&limit=50`
      : "/agent/audit-log/?limit=50",
    (raw) => AuditLogResponseSchema.parse(raw),
  );
}
